import { useEffect, useState, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaCheck } from "react-icons/fa";
import "../../../Style/waterloo-payroll/index.css";
import { formatDate } from "Helper/Converters";
import { exportFileToExcel } from "Utils/utils";

const YorkPerTicketReport = ({ show, handleClose, data, user }) => {
  const [sections, setSections] = useState([]);
  const [multipliers, setMultipliers] = useState({});
  const [showGrandTotal, setShowGrandTotal] = useState(false);
  const [savedMultipliers, setSavedMultipliers] = useState(null);

  const calculateSubtotal = (total, multiplier) => {
    if (total === undefined || total === null || total === "") {
      return 0;
    }
    const totalNum = parseFloat(total) || 0;
    const mult = parseFloat(multiplier) || 0;
    const result = totalNum * mult;
    return isNaN(result) ? 0 : result;
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(value)) {
      return "";
    }
    return `$${value.toFixed(0)}`;
  };

  const hasValue = (value) => {
    return value !== undefined && value !== null && value !== "";
  };

  const calculateGrandTotal = () => {
    const totals = {
      totalStandard: 0,
      totalAdditional: 0,
    };

    sections.forEach((section) => {
      const sectionMultipliers = multipliers[section.utilitiesCount];
      if (section?.totals && sectionMultipliers) {
        totals.totalStandard += calculateSubtotal(
          section.totals.totalStandard,
          sectionMultipliers.totalStandard
        );
        totals.totalAdditional += calculateSubtotal(
          section.totals.totalAdditional,
          sectionMultipliers.totalAdditional
        );
      }
    });

    return totals;
  };

  const validateGrandTotalGeneration = () => {
    for (const section of sections) {
      if (section?.totals) {
        const sectionMultipliers = multipliers[section.utilitiesCount] || {};

        if (
          hasValue(section.totals.totalStandard) &&
          parseFloat(section.totals.totalStandard) > 0
        ) {
          if (!hasValue(sectionMultipliers.totalStandard)) {
            return false;
          }
        }

        if (
          hasValue(section.totals.totalAdditional) &&
          parseFloat(section.totals.totalAdditional) > 0
        ) {
          if (!hasValue(sectionMultipliers.totalAdditional)) {
            return false;
          }
        }
      }
    }
    return true;
  };

  const getCurrentMultipliers = () => {
    return { ...multipliers };
  };

  const hasMultipliersChanged = () => {
    if (!savedMultipliers || !showGrandTotal) {
      return true;
    }

    const current = getCurrentMultipliers();
    const saved = savedMultipliers;

    // Compare all multipliers
    const allUtilitiesCounts = new Set([
      ...Object.keys(current),
      ...Object.keys(saved),
    ]);

    for (const utilitiesCount of allUtilitiesCounts) {
      const currentMulti = current[utilitiesCount] || {};
      const savedMulti = saved[utilitiesCount] || {};

      if (
        currentMulti.totalStandard !== savedMulti.totalStandard ||
        currentMulti.totalAdditional !== savedMulti.totalAdditional
      ) {
        return true;
      }
    }

    return false;
  };

  const handleGenerateGrandTotal = () => {
    if (!validateGrandTotalGeneration()) {
      alert("Please fill all multiplier fields before generating grand total.");
      return;
    }
    setSavedMultipliers(getCurrentMultipliers());
    setShowGrandTotal(true);
  };

  const resetAllStates = () => {
    setMultipliers({});
    setShowGrandTotal(false);
    setSavedMultipliers(null);
  };

  const handleModalClose = () => {
    resetAllStates();
    handleClose();
  };

  useEffect(() => {
    const yorkData = data?.york;
    if (yorkData?.sections) {
      setSections(yorkData.sections);
      // Initialize multipliers for each section
      const initialMultipliers = {};
      yorkData.sections.forEach((section) => {
        initialMultipliers[section.utilitiesCount] = {
          totalStandard: "",
          totalAdditional: "",
        };
      });
      setMultipliers(initialMultipliers);
    } else {
      setSections([]);
      setMultipliers({});
    }
    setShowGrandTotal(false);
    setSavedMultipliers(null);
  }, [data]);

  const grandTotal = useMemo(
    () => (showGrandTotal ? calculateGrandTotal() : null),
    [showGrandTotal, sections, multipliers]
  );

  const formatDataForExcel = () => {
    const formattedData = [
      [
        {
          v: "Station Code",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "Total Standard",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "Total Additional",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
      ],
    ];

    sections.forEach((section) => {
      if (section?.rows && section.rows.length > 0) {
        const label = `${section.utilitiesCount} Station Code's`;
        formattedData.push(
          [],
          [{ v: label, t: "s", s: { font: { bold: true } } }]
        );

        section.rows.forEach((row) => {
          formattedData.push([
            { v: row?.station || "YRF01" },
            { v: row?.totalStandard || 0 },
            { v: row?.totalAdditional || 0 },
          ]);
        });

        const sectionMultipliers = multipliers[section.utilitiesCount] || {};

        formattedData.push(
          [
            { v: "Total", t: "s", s: { font: { bold: true } } },
            {
              v: section.totals?.totalStandard || 0,
              s: { font: { bold: true } },
            },
            {
              v: section.totals?.totalAdditional || 0,
              s: { font: { bold: true } },
            },
          ],
          [
            { v: "Multiplier" },
            { v: sectionMultipliers.totalStandard || "" },
            { v: sectionMultipliers.totalAdditional || "" },
          ],
          [
            { v: "Sub Total", t: "s", s: { font: { bold: true } } },
            {
              v: formatCurrency(
                calculateSubtotal(
                  section.totals?.totalStandard,
                  sectionMultipliers.totalStandard
                )
              ),
              s: { font: { bold: true } },
            },
            {
              v: formatCurrency(
                calculateSubtotal(
                  section.totals?.totalAdditional,
                  sectionMultipliers.totalAdditional
                )
              ),
              s: { font: { bold: true } },
            },
          ]
        );
      }
    });

    if (grandTotal) {
      formattedData.push(
        [],
        [
          { v: "Grand Total", t: "s", s: { font: { bold: true } } },
          {
            v: formatCurrency(grandTotal.totalStandard),
            s: { font: { bold: true } },
          },
          {
            v: formatCurrency(grandTotal.totalAdditional),
            s: { font: { bold: true } },
          },
        ]
      );
    }

    return formattedData;
  };

  const handleExportToExcel = () => {
    if (!showGrandTotal) {
      alert(
        'Firstly, generate grand total by clicking on "Generate Grand Total" button'
      );
      return;
    }

    const excelData = formatDataForExcel();

    const startDate = user?.startDate
      ? new Date(user.startDate).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";
    const endDate = user?.endDate
      ? new Date(user.endDate).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";

    const fileName = `${user?.name || "Per Tickets"} Report ${startDate}${
      endDate ? `-${endDate}` : ""
    }`;

    exportFileToExcel(excelData, fileName);
  };

  const updateMultiplier = (utilitiesCount, field, value) => {
    setMultipliers((prev) => ({
      ...prev,
      [utilitiesCount]: {
        ...(prev[utilitiesCount] || { totalStandard: "", totalAdditional: "" }),
        [field]: value,
      },
    }));
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Per Ticket Report - {user?.name} {" ("}
          {formatDate(new Date(user?.endDate), "dd/MM/yyyy")} -{" "}
          {formatDate(new Date(user?.startDate), "dd/MM/yyyy")}
          {")"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "1rem", overflowX: "auto" }}>
        {!data || !sections || sections.length === 0 ? (
          <div className="text-center p-4">
            <p>No data available for the Per Ticket Report.</p>
            <p className="text-muted">
              Please ensure the report has been generated successfully.
            </p>
          </div>
        ) : (
          <div className="payroll-table-container" style={{ width: "100%" }}>
            {/* Mixed Section */}
            {/* <div
              style={{
                marginTop: "1rem",
                marginBottom: "1rem",
                backgroundColor: "#0068af",
                color: "white",
                padding: "12px 16px",
                borderRadius: "4px",
                fontWeight: "600",
                fontSize: "18px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              Mixed Units - Total Standard + Total Additional
            </div> */}

            {/* Render tables for each section */}
            {sections.map((section) => {
              const sectionMultipliers = multipliers[
                section.utilitiesCount
              ] || {
                totalStandard: "",
                totalAdditional: "",
              };
              const label = `${section.utilitiesCount} Station Code's`;

              return (
                <table key={section.utilitiesCount} className="payroll-table">
                  <thead>
                    <tr>
                      <th>{label}</th>
                      <th>Total Standard</th>
                      <th>Total Additional</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section?.rows?.map((row, index) => (
                      <tr key={index}>
                        <td>{row?.station || "YRF01"}</td>
                        <td>{row?.totalStandard}</td>
                        <td>{row?.totalAdditional}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td style={{ fontWeight: "bold" }}>Total</td>
                      <td>{section?.totals?.totalStandard}</td>
                      <td>{section?.totals?.totalAdditional}</td>
                    </tr>
                    <tr className="multiplier-row">
                      <td>Multiplier</td>
                      <td>
                        <input
                          type="number"
                          value={sectionMultipliers.totalStandard}
                          onChange={(e) =>
                            updateMultiplier(
                              section.utilitiesCount,
                              "totalStandard",
                              e.target.value
                            )
                          }
                          disabled={!hasValue(section?.totals?.totalStandard)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={sectionMultipliers.totalAdditional}
                          onChange={(e) =>
                            updateMultiplier(
                              section.utilitiesCount,
                              "totalAdditional",
                              e.target.value
                            )
                          }
                          disabled={!hasValue(section?.totals?.totalAdditional)}
                        />
                      </td>
                    </tr>
                    <tr className="subtotal-row">
                      <td style={{ fontWeight: "bold" }}>Sub Total</td>
                      <td>
                        {formatCurrency(
                          calculateSubtotal(
                            section?.totals?.totalStandard,
                            sectionMultipliers.totalStandard
                          )
                        )}
                      </td>
                      <td>
                        {formatCurrency(
                          calculateSubtotal(
                            section?.totals?.totalAdditional,
                            sectionMultipliers.totalAdditional
                          )
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              );
            })}

            {/* Grand Total Table */}
            {showGrandTotal && grandTotal && (
              <div style={{ marginTop: "3rem" }}>
                <div
                  style={{
                    marginBottom: "1rem",
                    backgroundColor: "#0068af",
                    color: "white",
                    padding: "12px 16px",
                    borderRadius: "4px",
                    fontWeight: "600",
                    fontSize: "18px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  Grand Total
                </div>
                <table className="payroll-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Total Standard</th>
                      <th>Total Additional</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ fontWeight: "bold" }}>Grand Total</td>
                      <td style={{ fontWeight: "bold" }}>
                        {formatCurrency(grandTotal.totalStandard)}
                      </td>
                      <td style={{ fontWeight: "bold" }}>
                        {formatCurrency(grandTotal.totalAdditional)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!showGrandTotal || hasMultipliersChanged() ? (
          <Button onClick={handleGenerateGrandTotal} variant="primary">
            Generate Grand Total
          </Button>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaCheck style={{ color: "green", fontSize: "18px" }} />
            <span style={{ color: "green", fontWeight: "500" }}>
              Grand Total Generated
            </span>
          </div>
        )}
        {showGrandTotal && !hasMultipliersChanged() && (
          <Button onClick={handleExportToExcel} variant="primary">
            Export
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default YorkPerTicketReport;
